/**
 * Display configuration for agent-service tools (e.g. filesystem agent).
 *
 * These tools are created dynamically at runtime and don't go through the
 * standard ToolRegistryManifest pipeline.  This lightweight config gives
 * the chat UI everything it needs to render pretty status text — including
 * dynamic argument summaries like the action or file path.
 */

export interface AgentServiceToolDisplay {
  prettyName: string;
  /**
   * Optional formatter that returns status text incorporating tool arguments.
   * `input` is the raw tool input from the event — typically a JsonValue object,
   * but may also be a JSON string or undefined.
   * Falls back to `prettyName` when not provided or when `input` is empty.
   */
  formatStatus?: (
    status: 'executing' | 'executed' | 'failed' | 'partial' | 'cancelled',
    input: unknown
  ) => string | undefined;
}

function resolveInput(input: unknown): Record<string, unknown> {
  if (!input) return {};
  if (typeof input === 'object' && !Array.isArray(input)) {
    return input as Record<string, unknown>;
  }
  if (typeof input === 'string') {
    try {
      return JSON.parse(input) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

// Leading verbs of compound-tool action ids (e.g. "create_prop",
// "query_components") mapped to their past / progressive forms so the chat
// chip can read "Created prop" / "Creating prop..." instead of the raw
// action id. Verbs not listed here (e.g. the "element_builder" pseudo-verb)
// fall back to the humanized action text as-is.
const ACTION_VERB_FORMS: Record<string, {past: string; progressive: string}> = {
  create: {past: 'Created', progressive: 'Creating'},
  update: {past: 'Updated', progressive: 'Updating'},
  set: {past: 'Set', progressive: 'Setting'},
  get: {past: 'Read', progressive: 'Reading'},
  query: {past: 'Searched', progressive: 'Searching'},
  list: {past: 'Listed', progressive: 'Listing'},
  insert: {past: 'Inserted', progressive: 'Inserting'},
  remove: {past: 'Removed', progressive: 'Removing'},
  delete: {past: 'Deleted', progressive: 'Deleting'},
  move: {past: 'Moved', progressive: 'Moving'},
  duplicate: {past: 'Duplicated', progressive: 'Duplicating'},
  rename: {past: 'Renamed', progressive: 'Renaming'},
  transform: {past: 'Transformed', progressive: 'Transforming'},
  reset: {past: 'Reset', progressive: 'Resetting'},
  unlink: {past: 'Unlinked', progressive: 'Unlinking'},
  unregister: {past: 'Unregistered', progressive: 'Unregistering'},
  reorder: {past: 'Reordered', progressive: 'Reordering'},
};

/**
 * Builds the display config for a page-automation compound tool (one
 * action-dispatch tool per family, e.g. components / elements). These tools
 * are MCP-discovered at runtime and have no static manifest, so without this
 * config the chat UI falls back to the raw tool id
 * ("page-automation-public-mcp-compound-components").
 *
 * The label is derived from the call's `action` argument: "create_prop" →
 * "Created prop" (executed) / "Creating prop..." (executing) / "Failed to
 * create prop" (failed). Actions whose first word is not a known verb render
 * as the humanized action text ("Element builder"). Calls without an action
 * fall back to `prettyName`.
 *
 * @param prettyName - family-level fallback label (e.g. "Component tools").
 * @returns the AgentServiceToolDisplay entry for the tool.
 */
function makeCompoundToolDisplay(prettyName: string): AgentServiceToolDisplay {
  return {
    prettyName,
    formatStatus: (status, input) => {
      const args = resolveInput(input);
      const action = typeof args.action === 'string' ? args.action : undefined;
      if (!action) return undefined;

      // "list-actions" is the schema-discovery meta-action — describe the
      // intent rather than the mechanics.
      if (action === 'list-actions') {
        switch (status) {
          case 'executing':
            return 'Checking available actions...';
          case 'executed':
            return 'Checked available actions';
          default:
            return undefined;
        }
      }

      // Builder actions have no leading verb; give them explicit labels.
      const builderLabels: Record<string, string> = {
        element_builder: 'elements',
        whtml_builder: 'elements from HTML',
        component_builder: 'component content',
      };
      const builderSubject = builderLabels[action];
      if (builderSubject) {
        switch (status) {
          case 'executing':
            return `Building ${builderSubject}...`;
          case 'executed':
            return `Built ${builderSubject}`;
          case 'failed':
            return `Failed to build ${builderSubject}`;
          default:
            return undefined;
        }
      }

      const [verb, ...rest] = action.split(/[_-]/);
      const restText = rest.join(' ');
      const forms = verb ? ACTION_VERB_FORMS[verb] : undefined;
      // Non-verb actions (element_builder, whtml_builder, ...): humanized
      // action text, capitalized ("Element builder").
      const humanized = action.replace(/[_-]/g, ' ');
      const fallbackLabel =
        humanized.charAt(0).toUpperCase() + humanized.slice(1);

      switch (status) {
        case 'executing':
          return forms
            ? `${forms.progressive} ${restText}...`
            : `${fallbackLabel}...`;
        case 'executed':
          return forms ? `${forms.past} ${restText}` : fallbackLabel;
        case 'failed':
          return forms
            ? `Failed to ${verb} ${restText}`
            : `${fallbackLabel} failed`;
        default:
          return undefined;
      }
    },
  };
}

/**
 * Builds the display config for a WFS file tool (write / edit).
 *
 * `tool_partial` streams arguments as a JSON fragment, so `path` is often not
 * parseable yet. Degrading to the generic noun keeps a usable label instead of
 * dropping to the `prettyName` noun phrase mid-stream.
 *
 * @param prettyName - fallback label when no status text applies.
 * @param verbs - the tool's verb forms, e.g. Writing / Wrote / write.
 * @returns the AgentServiceToolDisplay entry for the tool.
 */
function makeFileToolDisplay(
  prettyName: string,
  verbs: {progressive: string; past: string; infinitive: string}
): AgentServiceToolDisplay {
  return {
    prettyName,
    formatStatus: (status, input) => {
      const args = resolveInput(input);
      const filePath = typeof args.path === 'string' ? args.path : undefined;
      const target = filePath ? `\`${filePath}\`` : 'file';
      switch (status) {
        case 'executing':
          return `${verbs.progressive} ${target}`;
        case 'executed':
          return `${verbs.past} ${target}`;
        case 'failed':
          return `Failed to ${verbs.infinitive} ${target}`;
        default:
          return undefined;
      }
    },
  };
}

export const agentServiceToolDisplayConfig: Record<
  string,
  AgentServiceToolDisplay
> = {
  'page-automation-public-mcp-compound-components':
    makeCompoundToolDisplay('Component tools'),

  'page-automation-public-mcp-compound-elements':
    makeCompoundToolDisplay('Element tools'),

  'page-automation-public-mcp-create_reusable_section': {
    prettyName: 'Create section',
    formatStatus: (status) => {
      switch (status) {
        case 'executing':
          return 'Creating section...';
        case 'executed':
          return 'Created section';
        case 'failed':
          return 'Failed to create section';
        default:
          return undefined;
      }
    },
  },

  // Campaign brief read tool (CAM-853) — MCP-discovered, no static manifest.
  'get-campaign-brief': {prettyName: 'Get campaign brief'},

  // Campaign brief edit tool (CAM-853) — MCP-discovered, no static manifest.
  'edit-campaign-brief': {prettyName: 'Editing campaign brief'},

  // `getToolStatusText` prefers the tool input's intent before using this
  // fallback for persisted events that predate intent labels.
  'wfs-bash': {prettyName: 'Run command'},

  'wfs-commit': {
    prettyName: 'Commit changes',
    formatStatus: (status) => {
      switch (status) {
        case 'executing':
          return 'Committing changes';
        case 'executed':
          return 'Committed changes';
        case 'failed':
          return 'Failed to commit changes';
        default:
          return undefined;
      }
    },
  },

  'wfs-write': makeFileToolDisplay('Write file', {
    progressive: 'Writing',
    past: 'Wrote',
    infinitive: 'write',
  }),

  'wfs-edit': makeFileToolDisplay('Edit file', {
    progressive: 'Editing',
    past: 'Edited',
    infinitive: 'edit',
  }),
};
